<template>
    <div :style="{height: (maxHeight + 100) + 'px'}" v-if="data && show" style="user-select: none;">
        <highcharts :options="chartOptions" v-if="chartOptions.chart.type !== 'table'"></highcharts>
        <table-renderer :chart-options="chartOptions" v-else></table-renderer>
    </div>
</template>

<script>
    import {Chart} from 'highcharts-vue';
    import Highcharts from 'highcharts';
    import DataModule from 'highcharts/modules/data';

    DataModule(Highcharts);
    import TableRenderer from "./TableRenderer";

    export default {
        name: "ContentGraphRenderer",
        components: {
            highcharts: Chart,
            TableRenderer
        },
        props: {
            data: {
                required: true,
                type: Object
            },
            maxHeight: {
                type: [Number, String],
                default: 350
            },
            height: {
                type: Number,
                default: 350
            },
            rtl: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            show: false,
            numOfRows: null,
            numOfColumns: null,
            chartMap: {},
            chartOptions: {
                chart: {
                    type: '',
                    backgroundColor: 'transparent',
                    height: null
                },
                colors: [],
                title: {
                    text: '',
                    style: {
                        color: ''
                    },
                    useHTML: true
                },
                subtitle: {
                    text: '',
                    style: {
                        color: ''
                    },
                    useHTML: true
                },
                data: {
                    columns: []
                },
                tooltip: {
                    useHTML: true,
                    enabled: true
                },
                series: [],
                legend: {
                    itemStyle: {
                        color: '',
                        fontWeight: 'bold'
                    },
                    useHTML: true,
                    align: 'right',
                    verticalAlign: 'top',
                    layout: 'vertical',
                    symbolRadius: 0,
                    itemMarginTop: 2,
                    itemMarginBottom: 2,
                    borderColor: '#CCC',
                    borderWidth: 1,
                    floating: false,
                    rtl: false,
                    y: 50
                },
                plotOptions: {
                    series: {
                        pointPadding: 0.1,
                        groupPadding: 0.1
                    },
                    pie: {
                        dataLabels: {
                            color: 'white',
                            format: '{point.name} ({y}%)',
                            style: {
                                fontSize: '12px',
                                textOutline: '0px'
                            },
                            useHTML: true,
                        },
                        innerSize: null,
                        borderWidth: 0
                    },
                    line: {
                        dataLabels: [{
                            enabled: true,
                            color: 'white',
                            format: '{y}',
                            style: {
                                fontSize: '12px',
                                textOutline: '0px',
                            },
                            useHTML: true,
                        }],
                        lineWidth: 5,
                    },
                    bar: {
                        dataLabels: {
                            enabled: true,
                            inside: true,
                            color: 'white',
                            format: '{y}',
                            style: {
                                fontSize: '12px',
                                textOutline: '0px',
                                color: 'white'
                            },
                            useHTML: true,
                        },
                        borderColor: 'transparent',
                    },
                    area: {
                        dataLabels: [{
                            enabled: true,
                            color: 'black',
                            format: '{y}',
                            style: {
                                fontSize: '12px',
                                textOutline: '0px',
                            },
                            useHTML: true,
                        }],
                    },
                    column: {
                        dataLabels: {
                            enabled: true,
                            inside: true,
                            rotation: 270,
                            color: 'white',
                            format: '{y}',
                            style: {
                                fontSize: '12px',
                                textOutline: '0px'
                            },
                            useHTML: true,
                        },
                        borderColor: 'transparent',
                        stacking: 'normal'
                    },
                },
                yAxis: {
                    min: null,
                    max: null,
                    opposite: false,
                    labels: {
                        style: {
                            color: '',
                            fontSize: '12px'
                        },
                        useHTML: true,
                    },
                    title: {
                        text: '',
                        style: {
                            color: ''
                        },
                        useHTML: true
                    }
                },
                xAxis: {
                    reversed: false,
                    labels: {
                        style: {
                            color: '',
                            fontSize: '12px'
                        },
                        useHTML: true,
                    },
                    title: {
                        text: '',
                        style: {
                            color: ''
                        },
                        useHTML: true
                    }
                },
                credits: false
            }
        }),
        methods: {
            parseInner() {
                this.numOfRows = this.data ? this.data.contentgraphrows.length : 2;
                this.numOfColumns = this.data ? this.data.contentgraphrows[0].contentgraphvalues.length + 1 : 2;
                this.setChartOptions();
                this.setChartStyles();
                this.mapChartData();
            },
            mapChartData() {
                this.data.contentgraphrows.forEach((cgr, cgrIndex) => {
                    cgr.contentgraphvalues.forEach((cgv, cgvIndex) => {
                        this.$set(this.chartMap, `${cgrIndex}_${cgvIndex}`, {
                            id: cgv.contentgraphcolumnid,
                            value_id: cgv.contentgraphvalueid,
                            type: cgv.value ? 'value' : 'text',
                            text_id: cgv.context_textassetid ? cgv.context_textassetid : null,
                            text: cgv.context_textassetid ? this.$ta(cgv.context_textassetid) : null,
                            value: cgv.value ? cgv.value : null,
                            row: cgv.rownumber
                        });
                    });
                });
                this.chartMatrix();
            },
            setChartOptions() {
                this.chartOptions.chart.type = this.data.type;
                this.chartOptions.chart.height = this.height;
                this.chartOptions.title.text = this.$ta(this.data.title_textassetid);
                this.chartOptions.subtitle.text = this.$ta(this.data.subtitle_textassetid);
                this.chartOptions.xAxis.title.text = this.$ta(this.data.xlabel_textassetid);
                this.chartOptions.yAxis.title.text = this.$ta(this.data.ylabel_textassetid);
                this.chartOptions.yAxis.max = this.data.axis_max;
                this.chartOptions.yAxis.min = this.data.axis_min;
                this.chartOptions.plotOptions.column.stacking = this.data.stacking;
                if(this.chartOptions.chart.type === 'bar') {
                    this.chartOptions.xAxis.opposite = !!this.rtl;
                    this.chartOptions.yAxis.reversed = !!this.rtl;
                } else {
                    this.chartOptions.xAxis.reversed = !!this.rtl;
                    this.chartOptions.yAxis.opposite = !!this.rtl;
                }
                this.chartOptions.legend.rtl = !!this.rtl;
                this.$nextTick(() => {
                    this.$set(this.chartOptions.tooltip, 'enabled', this.data.type !== 'pie');
                    // this.tooltip.enabled = this.data.type !== 'pie';
                    this.data.regression && this.calculateRegressionLine();
                    this.data.cumulative && this.calculateCumulativeLine();
                });
            },
            setChartStyles() {
                this.$set(this.chartOptions, 'colors', ['#C5CBD3', '#1C5D99', '#8A817C', '#5D737E', '#B79A91', '#66658B', '#B5BA72', '#93B7BE', '#E8D264', '#EDC9FF']);
                this.$set(this.chartOptions.title.style, 'color', 'white');
                this.$set(this.chartOptions.subtitle.style, 'color', 'white');
                this.$set(this.chartOptions.legend.itemStyle, 'color', 'white');
                this.$set(this.chartOptions.xAxis.labels.style, 'color', 'white');
                this.$set(this.chartOptions.yAxis.labels.style, 'color', 'white');
                this.$set(this.chartOptions.xAxis.title.style, 'color', 'white');
                this.$set(this.chartOptions.yAxis.title.style, 'color', 'white');
            },
            chartMatrix() {
                let rows = [];
                for (let index = 0; index < this.numOfRows; index++) {
                    rows.push([]);
                    Object.values(this.chartMap).forEach((value) => {
                        if (value.row === (index + 1)) {
                            rows[index].push(value.value || value.text)
                        }
                    });
                }
                this.$set(this.chartOptions.xAxis, 'categories', rows[0].filter((val) => !!val));
                if (this.chartOptions.series.length === 0) {
                    if(this.chartOptions.chart.type !== 'pie' && this.chartOptions.chart.type !== 'table') {
                        rows.forEach((row, index) => {
                            if (index > 0) {
                                this.$set(this.chartOptions.series, this.chartOptions.series.length, {
                                    data: row.slice(1).map((val) => parseFloat(val)),
                                    name: row[0],
                                    lineWidth: 2,
                                    animation: {
                                        duration: 400
                                    }
                                });
                            }
                        });
                    } else if (this.chartOptions.chart.type === 'pie') {
                        this.$set(this.chartOptions.series, 0, {
                            type: 'pie',
                            data: [],
                            tooltip: {pointFormat: '<span style="color:{point.color}">●</span><b>{point.y}%</b><br/>'}
                        });
                        rows.forEach((row, rowIndex) => {
                            row.forEach((col, colIndex) => {
                                if(rowIndex === 0 && colIndex > 0) {
                                    this.$set(this.chartOptions.series[0].data, colIndex-1, [rows[0][colIndex], parseFloat(rows[1][colIndex])]);
                                }
                            });
                        });
                    } else if(this.chartOptions.chart.type === 'table') {
                        this.$set(this.chartOptions.data, 'columns', rows);
                    }
                }
                this.show = true;
                return rows;
            },
            calculateRegressionLine() {
                // if (state) {
                    let x = 0, y = 0, x_sum = 0, y_sum = 0, xx_sum = 0, xy_sum = 0;
                    const count = this.chartOptions.series[0].data.length;
                this.chartOptions.series.filter((row) => !row.dashStyle).forEach((row) => {
                        row.data.forEach((col, colIndex) => {
                            x = colIndex + 1;
                            y = parseFloat(col);
                            x_sum += x;
                            y_sum += y;
                            xx_sum += x * x;
                            xy_sum += x * y;
                        });
                        let m = 0, b = 0;
                        m = ((count * xy_sum) - (x_sum * y_sum)) / ((count * xx_sum) - (x_sum * x_sum));
                        b = ((y_sum / count) - ((m * x_sum) / count));
                        let result_values_y = [];
                        row.data.forEach((col, colIndex) => {
                            x = colIndex + 1;
                            y = ((x * m) + b);
                            result_values_y.push(parseFloat(y.toFixed(2)));
                        });
                        this.$set(this.chartOptions.series, this.chartOptions.series.length, {
                            type: 'line',
                            data: result_values_y,
                            name: row.name + ' ' + this.$t('graph.reg_line'),
                            dashStyle: 'dash',
                            lineWidth: 2,
                            dataLabels: [{enabled: false}],
                        });
                    });
                // } else {
                //     let ids = [];
                //     this.chartOptions.series.forEach((row) => {
                //         if(row.name.includes('Regression Line')) {
                //             ids.push(row.id)
                //         }
                //     });
                //     ids.forEach((id) => {
                //         this.$delete(this.chartOptions.series, this.chartOptions.series.findIndex((row) => row.id === id));
                //     });
                // }
            },
            calculateCumulativeLine() {
                // if (state) {
                this.chartOptions.series.filter((row) => !row.dashStyle).forEach((row) => {
                        let cumulative_array = [],
                            total = 0;
                        row.data.forEach((value) => {
                            total += parseFloat(value);
                            cumulative_array.push(total);
                        }, 0);
                        this.$set(this.chartOptions.series, this.chartOptions.series.length, {
                            type: 'line',
                            data: cumulative_array,
                            name: row.name + ' ' + this.$t('graph.cum_line'),
                            dashStyle: 'shortDot',
                            lineWidth: 2,
                            dataLabels: [{enabled: false}],
                        });
                    });
                // } else {
                //     let ids = [];
                //     this.chartOptions.series.forEach((row) => {
                //         if(row.name.includes('Cumulative Line')) {
                //             ids.push(row.id)
                //         }
                //     });
                //     ids.forEach((id) => {
                //         this.$delete(this.chartOptions.series, this.chartOptions.series.findIndex((row) => row.id === id));
                //     });
                // }
            },
        },
        mounted() {
            this.parseInner();
        }
    }
</script>

<style scoped>

</style>